import { useEffect, useMemo, useState } from "react";

import { useLazyQuery, useQuery } from "@apollo/client";
import moment from "moment-timezone";
import { useNavigate, useParams } from "react-router";

import { useWorkspaceSlug } from "@/components/helpers/custom-hooks/use-workspace-slug";
import canUseAllTimeslot from "@/components/helpers/meeting/can-use-all-timeslot";
import { buildUrl } from "@/components/helpers/url-utils";
import { isAssist } from "@/components/helpers/workspace/permissions";
import AuthenticateDialog from "@/components/pieces/auth/AuthenticateDialog";
import { SpecialtyRenderDataType } from "@/components/pieces/coaching/coach-profile/coachingUtils";
import { DEFAULT_CALENDAR_DAYS } from "@/components/pieces/schedule/SelectTimeSession/utils";
import BookingCoachBox from "@/components/pieces/talent-agents/BookingCoachBox";
import { getUser } from "@/components/session/JwtTokenManager";
import {
  AgentProfileQuery,
  CoachProductsQuery,
  GetCoachAllTimeslotsDocument,
  GetCoachAllTimeslotsQuery,
  GetCoachAvailableTimeslotsDocument,
  GetCoachAvailableTimeslotsQuery,
  WorkspaceMemberAssistantsDocument,
  WorkspaceMemberAssistantsQuery,
  WorkspaceMemberAssistantsQueryVariables,
} from "@/graphql";
import { getWorkspaceHost } from "@/lib/workspace-utilities";

// Update the component props interface
interface BookCoachProps {
  profile: Partial<AgentProfileQuery["agentProfile"]>;
  specialties: SpecialtyRenderDataType[];
  coachProducts: CoachProductsQuery["coachProducts"];
  coachSlug: string;
  isCoachOwner: boolean;
  selectedClientId?: string;
  onChangeClient?: (clientId?: string) => void;
  availableBooking?: boolean;
  availableChageProducts?: boolean;
  availableChangeSpecialties?: boolean;
}

export default function BookCoach({
  profile,
  specialties,
  coachProducts,
  coachSlug,
  isCoachOwner,
  selectedClientId,
  onChangeClient,
  availableBooking = true,
  availableChageProducts = true,
  availableChangeSpecialties = true,
}: BookCoachProps) {
  const loggedUser = getUser();
  const { data: assistantsData } = useQuery<
    WorkspaceMemberAssistantsQuery,
    WorkspaceMemberAssistantsQueryVariables
  >(WorkspaceMemberAssistantsDocument, {
    fetchPolicy: "cache-and-network",
    skip: !loggedUser || !isAssist(),
    variables: {
      customerId: loggedUser?.id,
    },
  });

  const assistantClients =
    assistantsData?.workspaceMember?.assistantClients || [];

  const assistantClientOptions = useMemo(
    () =>
      assistantClients
        .map((option) => ({
          value: option?.client?.user?.id,
          label: option?.client?.user?.name || option?.client?.user?.email,
        }))
        .concat([
          {
            value: getUser()?.id,
            label: "Me",
          },
        ]),
    [assistantClients],
  );

  const navigate = useNavigate();
  const { noWorkspace } = useParams();

  const [authDialogVisible, setAuthDialogVisible] = useState(false);
  const [selectedCoachProduct, setSelectedCoachProduct] = useState(null);
  const [selectedBookingTimes, setSelectedBookingTimes] = useState([]);
  const [bookingError, setBookingError] = useState(null);
  const [usedAllTimeslot, setUsedAllTimeslot] = useState(false);
  const [userTimeZone, setUserTimeZone] = useState("UTC");
  const coachId = profile?.user?.id;
  const coach = profile?.user;
  const roleView =
    isAssist() && selectedClientId !== loggedUser?.id
      ? `/assist/${selectedClientId}`
      : "";

  const firstTimeSlot = selectedBookingTimes?.[0]?.timeSlot;
  const timeslotsStrings = selectedBookingTimes.map((item) => {
    return `${item.timeSlot},${item.isPreferred ? "True" : "False"}`;
  });
  const createOrderUrl = buildUrl(
    `${roleView}/create-order/${coachId}/${selectedCoachProduct?.product?.id}`,
    {
      data: {
        back: `/@${coachSlug}`,
        startTime: firstTimeSlot,
        timeslots: timeslotsStrings,
        usedAllTimeslot: usedAllTimeslot,
      },
    },
  );

  const [loadTimeslotData, { data, loading: loadingTimeslotData }] =
    useLazyQuery<GetCoachAvailableTimeslotsQuery>(
      GetCoachAvailableTimeslotsDocument,
      {
        fetchPolicy: "network-only",
        onCompleted: (data) => {
          setUserTimeZone(data?.coachAvailableTimeslots?.timezone || "UTC");
        },
      },
    );

  const [
    loadAllTimeslotData,
    { data: allTimeslotData, loading: loadingAllTimeslotData },
  ] = useLazyQuery<GetCoachAllTimeslotsQuery>(GetCoachAllTimeslotsDocument, {
    fetchPolicy: "network-only",
  });

  const timeSlotData: string[] = usedAllTimeslot
    ? allTimeslotData?.coachAvailableTimeslots?.timeslots || []
    : data?.coachAvailableTimeslots?.timeslots || [];
  const availableTimeSlotData: string[] = canUseAllTimeslot(coach)
    ? data?.coachAvailableTimeslots?.timeslots || []
    : [];

  const fetchTimeslotData = (slug) => {
    if (!slug) {
      return;
    }
    const start = moment();
    loadTimeslotData({
      variables: {
        start: start,
        coachSlug: coachSlug,
        productSlug: slug,
        end: moment(start).add(DEFAULT_CALENDAR_DAYS, "days"),
        timeZone: moment.tz.guess(),
      },
    });

    if (usedAllTimeslot) {
      loadAllTimeslotData({
        variables: {
          start: start,
          coachSlug: coachSlug,
          productSlug: slug,
          end: moment(start).add(DEFAULT_CALENDAR_DAYS, "days"),
          timeZone: moment.tz.guess(),
        },
      });
    }
  };

  useEffect(() => {
    if (coachProducts?.length > 0 && !selectedCoachProduct) {
      setSelectedCoachProduct(coachProducts[0]);
    }
  }, [coachProducts]);

  useEffect(() => {
    if (selectedCoachProduct?.product?.slug) {
      fetchTimeslotData(selectedCoachProduct.product.slug);
    }
  }, [selectedCoachProduct?.product?.slug, usedAllTimeslot]);

  const loginAndCreateOrder = (login) => {
    // Login, switch to correct workspace, and create order
    const { currentWorkspaceDomain, currentWorkspaceRole, sesameQuery } = login;

    if (!sesameQuery) {
      window.location.href = WEB_APP + createOrderUrl;
      return true;
    }
    let redirectApp = WEB_APP;
    if (currentWorkspaceRole && !noWorkspace) {
      redirectApp = getWorkspaceHost(WEB_APP, currentWorkspaceDomain);
    }
    let redirectURL = redirectApp + createOrderUrl;
    // Remove the first character of the sesame query, which is a '?'
    redirectURL = redirectURL + "&" + sesameQuery.substring(1);
    window.location.href = redirectURL;
  };

  const onAuthCompleted = (response) => {
    if (!response) return;
    if (response.signUp) {
      navigate(createOrderUrl);
      return;
    }
    if (response.login) loginAndCreateOrder(response.login);
  };

  const onSubmit = () => {
    if (loggedUser) {
      navigate(createOrderUrl);
    } else {
      setAuthDialogVisible(true);
    }
  };

  const workspaceSlug = useWorkspaceSlug();

  return (
    <>
      <BookingCoachBox
        timeSlotData={timeSlotData}
        availableTimeSlotData={availableTimeSlotData}
        timeZone={userTimeZone}
        selectedCoachProduct={selectedCoachProduct}
        specialties={specialties}
        isCoachOwner={isCoachOwner}
        onChangeTimeZone={(option) => {
          setUserTimeZone(option.value);
        }}
        onChangeProduct={(product: CoachProductsQuery["coachProducts"][0]) => {
          setSelectedCoachProduct(
            coachProducts.find(
              (p) => String(p.product.id) === String(product.id),
            ),
          );
          setSelectedBookingTimes([]);
        }}
        onSubmit={onSubmit}
        loading={loadingTimeslotData || loadingAllTimeslotData}
        error={bookingError}
        onCancelError={() => setBookingError(null)}
        onConfirmError={() => setBookingError(null)}
        selectedBookingTimes={selectedBookingTimes}
        onSelectedBookingTimes={(timeSlots) => {
          const firstTimeSlot = timeSlots?.[0]?.timeSlot;
          if (firstTimeSlot && moment(firstTimeSlot).isValid()) {
            setSelectedBookingTimes(timeSlots);
          }
        }}
        assistantClientOptions={assistantClientOptions}
        onChangeClient={(option) => {
          onChangeClient(option?.value?.toString());
        }}
        selectedClientId={selectedClientId}
        availableBooking={availableBooking}
        profile={profile}
        availableChageProducts={availableChageProducts}
        availableChangeSpecialties={availableChangeSpecialties}
        usedAllTimeslot={usedAllTimeslot}
        coach={coach}
        onToggleUsedAllTimeslot={() => {
          setUsedAllTimeslot(!usedAllTimeslot);
          setSelectedBookingTimes([]);
        }}
      />
      <AuthenticateDialog
        onCompleted={onAuthCompleted}
        isOpen={authDialogVisible}
        onClose={() => setAuthDialogVisible(false)}
        signUpToText="to schedule your intro call"
        nextPath={null}
        initialForm={workspaceSlug ? "signin" : "signup"}
      />
    </>
  );
}
